import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import OpenFullscreen from '../../assets/icons/open-fullscreen.svg';
import CloseFullscreen from '../../assets/icons/close-fullscreen.svg';
import { ExaminerFunction } from '../../pages/Assessment/Assessment.types';
import { ExamScoringSchema, ExamTask, ExamTasksCoordinates } from '../../models/assessment.types';
import { useStyles } from './Preview.styles';
import { PreviewType, SolutionViewMode } from '../../pages/Solution/Solution.component';
import TooltipOne from '../TooltipOne/TooltipOne.component';
import { SolutionImageZoneWithImgData } from './SolutionZoneList/SolutionZoneList.types';
import { fabric } from 'fabric';
import _ from 'lodash';
import { fetchProtectedImg } from '../../utils/fetchProtectedImg';
import { ImageLoadErrorPage } from '../../pages/Solution/ImageLoadErrorPage/ImageLoadErrorPage.component';
import {
  Coords,
  pageImageData,
  Point,
  PreviewAction,
  PreviewActionType,
  rotateAngleValue,
  urlWithPageNumber,
  zoomFactorValue,
} from './Preview.types';
import { translateZoneToPixel } from '../../pages/Sessions/ImageZonesEditor/zoneHelpers';
import { imageZone } from '../../pages/Sessions/ImageZonesEditor/ZonesList/ZonesList.types';
import { PixelCrop } from 'react-image-crop';
import { IComment } from '../Comments/Comments.types';
import { AnnotationDefinition, AnnotationOnImage, AnnotationVersion } from '../../models/annotation.types';
import CommentsOnPageManager from './CommentsOnPageManager.component';
import AnnotationsOnPageManager, { AnnotationsOnPageManagerRef } from './AnnotationsOnPageManager.component';
import { SampleExamTaskGroupSolutionScoreState } from '../../services/scoringModel/scoringModel.types';
import { MouseButtons } from '../../const/mouse';
import { arePageImageDataEqual } from './previewUtils';
import { Status } from '../../services/exams/assessmentSession/assessments.types';
import { AnnotationsOnPageManagerContextProvider } from './AnnotationsOnPageManager.context';
import {
  FULLSCREEN_VIEW_TOP_MARGIN,
  IMAGE_OBJECT_NAME,
  IMAGE_ZONE_STROKE_WIDTH,
  KEY_SCROLL_DISTANSE,
  MARGIN_FOR_SCROLLBAR,
  PAGE_STROKE_WIDTH,
  SCROLLBAR_OFFSET_CHANGING_PAGE,
  VIEW_LEFT_MARGIN,
  VIEW_TOP_MARGIN,
} from './previewConsts';

interface PreviewProps {
  mode: SolutionViewMode;
  previewCardFullWidth: boolean;
  pagesUrls: string[];
  currentPage: number | undefined;
  setCurrentPage: (page: number) => void;
  zoomFactor: zoomFactorValue;
  rotateAngle: rotateAngleValue;
  viewSinglePage: boolean;
  viewTwoPages: boolean;
  isFullscreen: boolean;
  handleFullscreen: () => void;
  examinerFunction: ExaminerFunction;
  examTasksCoordinates: ExamTasksCoordinates | undefined;
  orgzId?: string;
  isCardPreview: boolean;
  isSolutionPreview: boolean;
  isAnnotationToolOpen: boolean;
  imageZones: SolutionImageZoneWithImgData[];
  isReadOnly: boolean;
  comments: IComment[];
  isNewCommentActive: boolean;
  commentsOnPageVisible: boolean;
  allCommentsOnPageVisible: boolean;
  onCommentChanged: (comment: IComment | null) => void;
  onAddAnnotation: (annotation: AnnotationOnImage) => void;
  onBeforeAddAnnotation: () => void;
  onAnnotationChanged: (annotations: AnnotationOnImage[]) => void;
  onAnnotationDeleted: (annotation: AnnotationOnImage) => void;
  onAnnotationsRefresh: (examTaskGroupSolutionScoreVersionId?: string) => void;
  onAnnotationDeactivate: () => void;
  isActiveAnnotationForGrading: boolean;
  canAddComment: boolean;
  canEditComment: boolean;
  annotations: AnnotationOnImage[];
  annotationVersions: AnnotationVersion[];
  annotationDefinitions: AnnotationDefinition[];
  annotationsVisible: boolean;
  activeTask: ExamTask | null;
  activeCriterion: ExamScoringSchema | null;
  canAddAnnotation: boolean;
  canEditRemoveAnnotation: boolean;
  activeAnnotationId: string;
  sampleExamTaskGroupSolutionScoreState: SampleExamTaskGroupSolutionScoreState | null;
  isAnnotationOpenAndDocked: boolean;
  canvasElementRef: RefObject<HTMLCanvasElement>;
  examSheetPagesLoaded: boolean;
  zoneCheckComplete: boolean;
  scoreState?: Status | null;
  setIsAnnotationSelected?: (isSelected: boolean) => void;
  isAnnotationSelected: boolean;
  selectPreviousActiveAnnotation?: () => void;
  isCommentOpen: boolean;
  setIsNewCommentActive: (value: boolean) => void;
  previewType: PreviewType;
  onImageLoadError: (failedItem: { url: string; callback: () => Promise<string> }) => void;
  onSetRotateAngle?: (angle: rotateAngleValue) => void;
  showErrorPage?: boolean;
  onRetryImageLoad?: () => void;
  onPagesLoadedToCanvas?: () => void;
  isZoneCheckCompleteRef: React.MutableRefObject<boolean>;
}

let arrowKeyPressHandler: ((event: KeyboardEvent) => void) | null = null;

const removeArrowKeyPressHandler = () => {
  if (arrowKeyPressHandler) {
    document.removeEventListener('keydown', arrowKeyPressHandler);
  }
};
export const Preview: React.FC<PreviewProps> = (props) => {
  const classes = useStyles();

  const canvasContainer = useRef<HTMLDivElement | null>(null);
  const popoversContainer = useRef<HTMLDivElement | null>(null);
  const canvasItemsBoundary = useRef<{
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } | null>(null);

  const annotationsOnPageManagerRef = useRef<AnnotationsOnPageManagerRef | undefined>(undefined);

  const activeImageBorderColor = '#3C419A';
  const defaultImageBorderColor = 'rgba(44, 45, 62, 0.3)';
  const shouldDisplayImageZones = props.isSolutionPreview && props.imageZones.length > 0;
  const topCanvasMargin = props.previewCardFullWidth ? FULLSCREEN_VIEW_TOP_MARGIN : VIEW_TOP_MARGIN;

  const [hiddenContainerHeight, setHiddenContainerHeight] = useState<string>('calc(100% - 85px)');
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const canvas = useRef<fabric.Canvas | null>(null);
  const [currentCardContainerDimensions, setCurrentCardContainerDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [canvasZoom, setCanvasZoom] = useState<number | undefined>(undefined);
  const pageImages = useRef<pageImageData[]>([]);
  const currentLoadedPageUrls = useRef<urlWithPageNumber[]>([]);
  const imagesToBeLoadedCount = useRef<number | null>(null);
  const processedImagesCount = useRef<number>(0);
  const failedImageUrls = useRef<string[]>([]);
  const [pageAreAddedToCanvas, setPageAreAddedToCanvas] = useState<boolean>(false);
  const [isCanvasReady, setIsCanvasReady] = useState<boolean>(false);
  const [actionsToTrigger, setActionsToTrigger] = useState<PreviewAction[]>([]);
  const [isPageOverflow, setIsPageOverflow] = useState<boolean>(false);
  const [wheelDeltaY, setWheelDeltaY] = useState<number>(0);
  const [isCanvasHeightAdjusted, setIsCanvasHeightAdjusted] = useState<boolean>(false);

  const isDraggingRef = useRef(false);
  const lastXRef = useRef(0);
  const lastYRef = useRef(0);
  const currentRotateAngle = useRef<rotateAngleValue>(props.rotateAngle);
  const currentCoords = useRef<Coords | null>(null);
  const isMouseScrollDisabled = useRef<boolean>(false);

  useEffect(() => {
    setTimeout(() => {
      const upperCanvas = document.getElementsByClassName('upper-canvas');
      if (upperCanvas && upperCanvas.length > 0) {
        const firstElement = upperCanvas[0] as HTMLCanvasElement;
        if (firstElement) {
          firstElement.style.marginTop = `${
            props.previewCardFullWidth ? FULLSCREEN_VIEW_TOP_MARGIN : VIEW_TOP_MARGIN
          }px`;
        }
      }
    }, 200);
  }, [props.previewCardFullWidth]);

  useEffect(() => {
    if (!props.isNewCommentActive) return;
    const handleMouseMove = (e: MouseEvent) => {
      const element = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      if (element) {
        const cursor = getComputedStyle(element).cursor;
        if (cursor === 'auto') {
          props.setIsNewCommentActive(false);
          return;
        }
      }
    };

    document.addEventListener('mousedown', handleMouseMove);

    return () => {
      document.removeEventListener('mousedown', handleMouseMove);
    };
  }, [props.isNewCommentActive]);

  useEffect(() => {
    currentRotateAngle.current = props.rotateAngle;
  }, [props.rotateAngle]);

  useEffect(() => {
    if (props.isFullscreen) {
      removeArrowKeyPressHandler();
      if (props.isAnnotationSelected) return;
      arrowKeyPressHandler = (event: KeyboardEvent) => {
        if (canvasContainer.current) {
          switch (event.key) {
            case 'ArrowUp': {
              event.preventDefault();
              canvasContainer.current.scrollTo({ top: canvasContainer.current.scrollTop - KEY_SCROLL_DISTANSE });
              break;
            }
            case 'ArrowDown': {
              event.preventDefault();
              canvasContainer.current.scrollTo({ top: canvasContainer.current.scrollTop + KEY_SCROLL_DISTANSE });
              break;
            }
          }
        }
      };

      document.addEventListener('keydown', arrowKeyPressHandler);
    } else {
      removeArrowKeyPressHandler();
    }
    return () => {
      removeArrowKeyPressHandler();
    };
  }, [props.isFullscreen, props.isAnnotationSelected]);

  const getOffsetForPopovers = (): Coords => {
    if (!props.canvasElementRef.current || !popoversContainer.current) {
      throw new Error('empty canvas!');
    }

    const offset = fabric.util.getElementOffset(props.canvasElementRef.current);
    let box: DOMRect | undefined = undefined;
    if (typeof popoversContainer.current.getBoundingClientRect !== 'undefined') {
      box = popoversContainer.current.getBoundingClientRect();
    }
    return {
      left: offset.left - (box ? box.left : 0),
      top: offset.top - (box ? box.top : 0),
    };
  };

  const getCurrentStrokeWidth = (zoom: number): number => {
    return shouldDisplayImageZones ? IMAGE_ZONE_STROKE_WIDTH : PAGE_STROKE_WIDTH / zoom;
  };

  const getZoomToFitWidth = (width: number) => {
    if (!canvas.current || !canvasContainer.current) return;
    const margins = (props.viewTwoPages ? 4 : 2) * VIEW_LEFT_MARGIN + MARGIN_FOR_SCROLLBAR;
    return canvasContainer.current.clientWidth / (width + margins);
  };

  const getZoomToFitHeight = (height: number) => {
    setIsCanvasHeightAdjusted(true);
    if (!canvas.current || !canvasContainer.current) return;
    return (canvasContainer.current.clientHeight - topCanvasMargin) / height;
  };

  useEffect(() => {
    if (canvas.current && canvasZoom !== undefined) {
      canvas.current.setZoom(canvasZoom);
      const newStrokeWidth = getCurrentStrokeWidth(canvasZoom);
      pageImages.current.forEach((obj: pageImageData) => {
        obj.image.set('strokeWidth', newStrokeWidth);
      });
    }
  }, [canvasZoom]);

  const adjustCanvasZoom = (): number | undefined => {
    if (!canvas.current && isCanvasHeightAdjusted) return;
    if (canvasContainer.current) {
      let newZoom;
      if (canvasItemsBoundary.current) {
        const { minX, maxX, minY, maxY } = canvasItemsBoundary.current;
        newZoom = props.zoomFactor === zoomFactorValue.screenWidth ? getZoomToFitWidth(maxX - minX) : undefined;
        if (props.zoomFactor === zoomFactorValue.screenWidthAndHeight) {
          newZoom =
            maxY - minY > maxX - minX || maxY - minY > canvasContainer.current.clientHeight
              ? getZoomToFitHeight(maxY - minY)
              : getZoomToFitWidth(maxX - minX);
        }
      }

      if (!newZoom || isNaN(newZoom)) {
        newZoom = props.zoomFactor / 4;
      }
      setIsCanvasHeightAdjusted(false);
      setCanvasZoom(newZoom);
      return newZoom;
    }
  };

  const resetCanvasPosition = () => {
    if (canvas.current && canvas.current.viewportTransform) {
      const currentTransform = canvas.current.viewportTransform.slice();
      currentTransform[4] = 0;
      currentTransform[5] = 0;
      canvas.current.setViewportTransform(currentTransform);
    }
  };

  const updateCanvasDimensions = _.debounce(() => {
    if (!canvas.current || !canvasContainer.current) return;
    resetCanvasPosition();
    let newCanvasWidth = canvasContainer.current.clientWidth - (props.isAnnotationToolOpen ? 120 : 0);
    let newCanvasHeight = canvasContainer.current.clientHeight;
    if (canvasItemsBoundary.current) {
      const { minX, maxX, minY, maxY } = canvasItemsBoundary.current;
      newCanvasWidth = Math.max(maxX - minX, newCanvasWidth);
      newCanvasHeight = Math.max(maxY - minY, newCanvasHeight);
    }
    const zoom = adjustCanvasZoom() || canvas.current.getZoom();
    const newWidth = (newCanvasWidth - MARGIN_FOR_SCROLLBAR) * zoom;
    const newHeight = newCanvasHeight * zoom;
    if (canvas.current.width !== newWidth || canvas.current.height !== newHeight) {
      canvas.current.setWidth(newWidth);
      canvas.current.setHeight(newHeight);
    }
  }, 200);

  function getOffsetsForRotation(image: fabric.Object): { leftOffset: number; topOffset: number } {
    switch (currentRotateAngle.current) {
      case 90:
        return { leftOffset: image.height ?? 0, topOffset: 0 };
      case 180:
        return { leftOffset: image.width ?? 0, topOffset: image.height ?? 0 };
      case 270:
        return { leftOffset: 0, topOffset: image.width ?? 0 };
      default:
        return { leftOffset: 0, topOffset: 0 };
    }
  }

  const setMinValue = (currentValue: number | undefined, newValue: number) =>
    currentValue === undefined ? newValue : Math.min(currentValue, newValue);
  const setMaxValue = (currentValue: number | undefined, newValue: number) =>
    currentValue === undefined ? newValue : Math.max(currentValue, newValue);

  function getObjectDimensions(object: fabric.Object): { left: number; right: number; top: number; bottom: number } {
    if (
      object.left !== undefined &&
      object.top !== undefined &&
      object.width !== undefined &&
      object.height !== undefined
    ) {
      if (currentRotateAngle.current === 0) {
        return {
          left: object.left,
          right: object.left + object.width,
          top: object.top,
          bottom: object.top + object.height,
        };
      }
      if (currentRotateAngle.current === 90) {
        return {
          left: object.left - object.height,
          right: object.left,
          top: object.top,
          bottom: object.top + object.width,
        };
      }
      if (currentRotateAngle.current === 180) {
        return {
          left: object.left - object.width,
          right: object.left,
          top: object.top - object.height,
          bottom: object.top,
        };
      }

      return {
        left: object.left,
        right: object.left + object.height,
        top: object.top - object.width,
        bottom: object.top,
      };
    } else {
      return { left: 0, right: 0, top: 0, bottom: 0 };
    }
  }

  const updateCanvasItemsBoundary = () => {
    let minX: number | undefined = undefined;
    let maxX: number | undefined = undefined;
    let minY: number | undefined = undefined;
    let maxY: number | undefined = undefined;

    canvas.current?.forEachObject((item) => {
      if (
        item.left !== undefined &&
        item.top !== undefined &&
        item.width !== undefined &&
        item.height !== undefined &&
        item.name === IMAGE_OBJECT_NAME
      ) {
        const { left, right, top, bottom } = getObjectDimensions(item);
        minX = setMinValue(minX, left);
        maxX = setMaxValue(maxX, right);
        minY = setMinValue(minY, top);
        maxY = setMaxValue(maxY, bottom);
      }
    });
    if (minX !== undefined && maxX !== undefined && minY !== undefined && maxY !== undefined) {
      canvasItemsBoundary.current = { minX, maxX, minY, maxY };
    } else {
      canvasItemsBoundary.current = null;
    }
    updateCanvasDimensions();
  };

  const addToCanvas = (obj: fabric.Object) => {
    if (!canvas.current) return;
    canvas.current.add(obj);
  };

  const getPageImageCoords = (index: number, oneUnderAnother = false): Coords => {
    const pageImage = pageImages.current[index - 1];
    if (oneUnderAnother) {
      if (index === 0) {
        return {
          left: 0,
          top: 0,
        };
      } else {
        return {
          left: 0,
          top: pageImage && pageImage.image && pageImage.image.aCoords ? pageImage.image.aCoords.bl.y : 0,
        };
      }
    }

    if (index === 0) {
      return {
        left: 0,
        top: 0,
      };
    } else {
      return {
        left: pageImage && pageImage.image && pageImage.image.aCoords ? pageImage.image.aCoords.tr.x : 0,
        top: 0,
      };
    }
  };

  const getImageZoneClipPathData = (
    width: number | undefined,
    height: number | undefined,
    clipZone?: imageZone | undefined,
  ): { clipPath: fabric.Rect; pixelCrop: PixelCrop } | undefined => {
    if (!width || !height || !clipZone) return undefined;

    const pixelCrop = translateZoneToPixel(width, height, clipZone);
    const clipPath = new fabric.Rect({
      left: -(pixelCrop.width + pixelCrop.x) / 2,
      top: -(pixelCrop.height + pixelCrop.y) / 2,
      width: pixelCrop.width + pixelCrop.x,
      height: pixelCrop.height + pixelCrop.y,
    });
    return { clipPath, pixelCrop };
  };

  const updateClipPath = useCallback(() => {
    if (pageAreAddedToCanvas && canvas.current && pageImages.current) {
      const shapes: string[] = [];
      pageImages.current.forEach((image) => {
        const { left, right, top, bottom } = getObjectDimensions(image.image);
        shapes.push(`M${left} ${top} h ${right - left} v ${bottom - top} h -${right - left} v ${bottom - top}`);
      });
      const clipPath = new fabric.Path(shapes.join(' '), {
        absolutePositioned: true,
        originX: 'center',
        originY: 'center',
      });
      canvas.current.clipPath = clipPath;
    } else if (canvas.current) {
      canvas.current.clipPath = undefined;
    }
  }, [pageAreAddedToCanvas]);

  useEffect(() => {
    updateClipPath();
  }, [updateClipPath]);

  const rotatePageCoords = (image: fabric.Object): Coords => {
    image.rotate(currentRotateAngle.current);
    const newCoords = getPageImageCoords(0);

    const { leftOffset, topOffset } = getOffsetsForRotation(image);
    newCoords.left += leftOffset;
    newCoords.top += topOffset;
    image.set({ ...newCoords });
    currentCoords.current = newCoords;
    updateClipPath();
    return newCoords;
  };

  const addPagesToCanvas = () => {
    const objectsOnCanvas = canvas.current ? canvas.current.getObjects() : [];
    pageImages.current.forEach((imageData: pageImageData, index: number) => {
      imageData.image.set({
        ...getPageImageCoords(index, !!imageData.pixelCrop),
        name: IMAGE_OBJECT_NAME,
      });
      if (!objectsOnCanvas.find((item) => item === imageData.image)) {
        if (props.viewSinglePage && pageImages.current.length === 1 && currentRotateAngle.current !== 0) {
          rotatePageCoords(imageData.image);
        }
        addToCanvas(imageData.image);
        imageData.image.sendToBack();
      }
    });
    updateCanvasItemsBoundary();
    adjustCanvasZoom();
    setPageAreAddedToCanvas(true);
    props.onPagesLoadedToCanvas?.();
  };

  const clearPageImages = (): void => {
    canvas.current?.getObjects().forEach((item) => {
      canvas.current?.remove(item);
    });
    pageImages.current = [];
    processedImagesCount.current = 0;
    failedImageUrls.current = [];
    setPageAreAddedToCanvas(false);
  };

  useEffect(() => {
    if (!props.examSheetPagesLoaded && currentLoadedPageUrls.current.length) {
      currentLoadedPageUrls.current = [];
      clearPageImages();
    }
  }, [props.examSheetPagesLoaded, props.pagesUrls]);

  const addLoadedPageImage = (
    objectURL: string,
    orderIndex: number,
    pageNumber: number,
    clipZone?: imageZone | undefined,
  ) => {
    console.log('addLoadedPageImage zones blocker', { zoneCheckComplete: props.isZoneCheckCompleteRef.current });
    if (!props.isZoneCheckCompleteRef.current) return;
    if (!canvas.current) return;
    fabric.Image.fromURL(objectURL, function (img) {
      const clipPathData = getImageZoneClipPathData(img.width, img.height, clipZone);

      const pageCoords = img.getCoords(true, false);
      img.set({
        hasControls: false,
        selectable: false,
        width: clipPathData?.pixelCrop?.width ? clipPathData.pixelCrop.width : img.width,
        height: clipPathData?.pixelCrop?.height ? clipPathData.pixelCrop.height : img.height,
        clipPath: clipPathData ? clipPathData.clipPath : undefined,
        cropX: clipPathData ? clipPathData.pixelCrop.x : 0,
        cropY: clipPathData ? clipPathData.pixelCrop.y : 0,
        stroke: defaultImageBorderColor,
        strokeWidth: getCurrentStrokeWidth(canvas.current?.getZoom() || 1),
      });
      const newItem = {
        image: img,
        orderIndex,
        pageNumber,
        pixelCrop: clipPathData?.pixelCrop,
        pageCoords,
      };
      const foundItem = pageImages.current.find((item) => arePageImageDataEqual(newItem, item));
      if (!foundItem) {
        pageImages.current = [...pageImages.current, newItem].sort((a, b) => a.orderIndex - b.orderIndex);
      }
      processedImagesCount.current++;
      if (processedImagesCount.current === imagesToBeLoadedCount.current) {
        addPagesToCanvas();
      }
    });
  };

  const getPopoverCoord = (left: number, top: number, zoom: number, offset: Coords): Point => {
    return {
      x: left * zoom + offset.left,
      y: top * zoom + topCanvasMargin + offset.top,
    };
  };

  const getCanvasPoint = (clientX: number, clientY: number): Point => {
    if (!props.canvasElementRef.current || !canvas.current) {
      throw new Error('empty canvas!');
    }
    const zoom = canvas.current.getZoom();
    const offset = fabric.util.getElementOffset(props.canvasElementRef.current);
    return { x: (clientX - offset.left - VIEW_LEFT_MARGIN) / zoom, y: (clientY - offset.top) / zoom };
  };

  useEffect(() => {
    if (!canvasContainer.current) return;
    const observer = new ResizeObserver(
      _.debounce(() => {
        if (canvasContainer.current) {
          if (
            canvasContainer.current?.clientWidth !== currentCardContainerDimensions?.width ||
            canvasContainer.current?.clientHeight !== currentCardContainerDimensions.height
          )
            setCurrentCardContainerDimensions({
              width: canvasContainer.current?.clientWidth,
              height: canvasContainer.current?.clientHeight,
            });
        }
      }, 100),
    );
    observer.observe(canvasContainer.current);
    return () => {
      if (canvasContainer.current) {
        observer?.unobserve(canvasContainer.current);
      }
    };
  }, []);

  useEffect(() => {
    pageImages.current.forEach((obj: pageImageData, index: number) => {
      if (props.currentPage === undefined) return;
      const newStrokeColor =
        index === props.currentPage - 1 &&
        props.pagesUrls[props.currentPage - 1] &&
        selectedPages.includes(props.pagesUrls[props.currentPage - 1].slice(-1))
          ? activeImageBorderColor
          : defaultImageBorderColor;
      obj.image.set('stroke', newStrokeColor);
    });
  }, [selectedPages, props.currentPage, props.pagesUrls]);

  const addPages = (urls: urlWithPageNumber[]) => {
    if (!canvas.current) {
      return;
    }

    currentLoadedPageUrls.current = urls;
    imagesToBeLoadedCount.current = urls.length;
    processedImagesCount.current = 0;
    clearPageImages();
    urls.forEach((url: urlWithPageNumber, index: number) => {
      fetchProtectedImg(url.url)
        .then((objectUrl: string) => {
          addLoadedPageImage(objectUrl, index, url.pageNumber, undefined);
        })
        .catch(() => {
          processedImagesCount.current++;
          failedImageUrls.current.push(url.url);
          props.onImageLoadError({ url: url.url, callback: () => fetchProtectedImg(url.url) });
          if (processedImagesCount.current === imagesToBeLoadedCount.current) {
            if (pageImages.current.length > 0 && failedImageUrls.current.length === 0) {
              addPagesToCanvas();
            } else if (failedImageUrls.current.length > 0) {
            } else {
              props.onPagesLoadedToCanvas?.();
            }
          }
        });
    });
  };

  const addImageZones = _.debounce(() => {
    imagesToBeLoadedCount.current = props.imageZones.length;
    clearPageImages();
    if (props.examSheetPagesLoaded && props.zoneCheckComplete) {
      props.imageZones.forEach((zone: SolutionImageZoneWithImgData, index: number) => {
        const objectUrl = window.URL.createObjectURL(zone.wholeImageData);
        addLoadedPageImage(objectUrl, index, zone.zone.pageNumber, zone.zone);
      });
    }
  }, 500);

  useEffect(() => {
    if (!canvas.current || !props.pagesUrls.length || props.currentPage === undefined) {
      return;
    }
    if (shouldDisplayImageZones && props.imageZones.length > 0) {
      clearPageImages();
      if (currentLoadedPageUrls.current.length) {
        currentLoadedPageUrls.current = [];
      }
      props.setCurrentPage(props.imageZones[0].zone.pageNumber);
      addImageZones();
    } else {
      if (!props.zoneCheckComplete) {
        return;
      }

      if (props.pagesUrls.length) {
        const newPageUrls: urlWithPageNumber[] = [];

        if (props.viewSinglePage) {
          newPageUrls.push({ url: props.pagesUrls[props.currentPage - 1], pageNumber: props.currentPage });
        } else if (props.viewTwoPages) {
          newPageUrls.push({ url: props.pagesUrls[props.currentPage - 1], pageNumber: props.currentPage });
          newPageUrls.push({
            url: props.pagesUrls[props.currentPage === props.pagesUrls.length ? 0 : props.currentPage],
            pageNumber: props.currentPage + 1,
          });
        }
        if (!_.isEqual(currentLoadedPageUrls.current, newPageUrls)) {
          if (
            !(
              props.imageZones.length === 0 &&
              currentLoadedPageUrls.current.length === 0 &&
              newPageUrls.length !== 0 &&
              newPageUrls[0]?.pageNumber !== 1
            )
          ) {
            addPages(newPageUrls);
          }
        } else {
        }
      }
    }
  }, [
    props.pagesUrls,
    props.currentPage,
    props.viewSinglePage,
    props.viewTwoPages,
    props.isCardPreview,
    shouldDisplayImageZones,
    props.imageZones,
    props.zoneCheckComplete,
    isCanvasReady,
  ]);

  useEffect(() => {
    if (
      !pageAreAddedToCanvas &&
      pageImages.current.length &&
      ((shouldDisplayImageZones && props.imageZones.length && pageImages.current.length === props.imageZones.length) ||
        (!shouldDisplayImageZones &&
          currentLoadedPageUrls.current.length &&
          pageImages.current.length === currentLoadedPageUrls.current.length))
    ) {
      addPagesToCanvas();
    }
  }, [shouldDisplayImageZones, props.imageZones, pageAreAddedToCanvas]);

  useEffect(() => {
    if (!canvas.current) return;
    updateCanvasItemsBoundary();
  }, [
    props.zoomFactor,
    currentCardContainerDimensions,
    props.previewCardFullWidth,
    props.isFullscreen,
    props.isAnnotationToolOpen,
    props.isAnnotationOpenAndDocked,
    props.isCommentOpen,
  ]);

  useEffect(() => {
    if (!canvas.current) return;
    if (props.viewSinglePage && pageImages.current.length) {
      const image = pageImages.current[0].image;

      const newCoords = rotatePageCoords(image);

      const rotateCommentAction: PreviewAction<Coords> = { type: PreviewActionType.rotateComments, payload: newCoords };
      annotationsOnPageManagerRef.current?.rotateAnnotations(newCoords, props.rotateAngle);
      setActionsToTrigger((prevState) => [...prevState, rotateCommentAction]);
      updateCanvasItemsBoundary();
    }
  }, [props.rotateAngle]);

  useEffect(() => {
    const pages: string[] = [];
    if (props.examTasksCoordinates) {
      props.examTasksCoordinates.examSheetPages.forEach((page) => {
        pages.push(page.pageNumber.toString());
      });
      setSelectedPages(pages);
      props.setCurrentPage(pages.length ? parseInt(pages[0]) : 1);
    }
  }, [props.examTasksCoordinates]);

  useEffect(() => {
    const canvasObject = new fabric.Canvas(props.canvasElementRef.current, {
      renderOnAddRemove: true,
      selection: false,
      fireRightClick: true,
      stopContextMenu: true,
      uniformScaling: false,
    });

    canvas.current = canvasObject;
    setIsCanvasReady(true);
    return () => {
      setIsCanvasReady(false);
      canvas.current && canvas.current.dispose();
    };
  }, []);

  const handleActionTrigger = (action: PreviewAction) => {
    setActionsToTrigger((prevState) => prevState.filter((item) => item !== action));
  };

  const isScrollbarNotVisible = () => {
    const scrollArea = canvasContainer.current;
    return (scrollArea && scrollArea.scrollHeight === scrollArea.clientHeight) ?? false;
  };

  const handleNewPageOnScroll = () => {
    if (canvasContainer.current && props.isCardPreview) {
      const scrollArea = canvasContainer.current;

      if (scrollArea.offsetHeight + scrollArea.scrollTop + SCROLLBAR_OFFSET_CHANGING_PAGE > scrollArea.scrollHeight) {
        if (props.currentPage && props.currentPage < props.pagesUrls.length) {
          setIsPageOverflow(true);
        }
        if (props.currentPage && props.currentPage === props.pagesUrls.length) {
          props.setCurrentPage(props.currentPage);
        }
      }
      if (scrollArea.scrollTop === 0) {
        if (props.currentPage && props.currentPage > 1) {
          setIsPageOverflow(true);
        }
      }
    }
  };

  const handleScroll = () => {
    setActionsToTrigger((prevState) => [...prevState, { type: PreviewActionType.moveCommentsPopovers }]);
    if (!isScrollbarNotVisible() && !isPageOverflow) {
      handleNewPageOnScroll();
    }
  };

  const handleNavigation = (e: any) => {
    setTimeout(() => {
      if (props.isCardPreview) {
        setWheelDeltaY(wheelDeltaY + e.wheelDeltaY);
        if (wheelDeltaY > 150) {
          if (props.currentPage && props.currentPage > 1) {
            props.setCurrentPage(props.currentPage - 1);
            setWheelDeltaY(0);
          }
        } else if (wheelDeltaY < -150) {
          if (props.currentPage && props.currentPage < props.pagesUrls.length) {
            props.setCurrentPage(props.currentPage + 1);
            setWheelDeltaY(0);
          }
        }
      }
    }, 250);
  };

  useEffect(() => {
    if (!isScrollbarNotVisible()) return;
    window.addEventListener('wheel', handleNavigation);

    return () => {
      window.removeEventListener('wheel', handleNavigation);
    };
  }, [handleNavigation]);

  const handleOverflowNavigation = (e: any) => {
    if (canvasContainer.current && props.isCardPreview) {
      const scrollArea = canvasContainer.current;
      setWheelDeltaY(wheelDeltaY + e.wheelDeltaY);
      if (wheelDeltaY > 900) {
        if (props.currentPage && props.currentPage > 1) {
          // need to reset the angle before switching page to display page with proper rotation
          currentRotateAngle.current = 0;
          props.setCurrentPage(props.currentPage - 1);
          setTimeout(() => {
            // need to set the proper angle after recreating new page (but not with the page to avoid double page rebuild)
            props.onSetRotateAngle?.(0);
            scrollArea.scrollTo({
              top: scrollArea.scrollHeight - scrollArea.offsetHeight - SCROLLBAR_OFFSET_CHANGING_PAGE,
              left: 0,
            });
          }, 500);
          setIsPageOverflow(false);
          setWheelDeltaY(0);
        }
      } else if (wheelDeltaY < -900) {
        if (props.currentPage && props.currentPage < props.pagesUrls.length) {
          // need to reset the angle before switching page to display page with proper rotation
          currentRotateAngle.current = 0;
          props.setCurrentPage(props.currentPage + 1);

          setTimeout(() => {
            // need to set the proper angle after recreating new page (but not with the page to avoid double page rebuild)
            props.onSetRotateAngle?.(0);
            scrollArea.scrollTo({ top: 5, left: 0 });
          }, 500);
          setIsPageOverflow(false);
          setWheelDeltaY(0);
        }
      }
    }
  };

  useEffect(() => {
    if (!isPageOverflow) return;
    window.addEventListener('wheel', handleOverflowNavigation);

    return () => {
      window.removeEventListener('wheel', handleOverflowNavigation);
    };
  }, [handleOverflowNavigation]);

  useEffect(() => {
    setActionsToTrigger((prevState) => [...prevState, { type: PreviewActionType.moveCommentsPopovers }]);
  }, [
    props.allCommentsOnPageVisible,
    props.zoomFactor,
    props.rotateAngle,
    props.currentPage,
    props.viewSinglePage,
    props.viewTwoPages,
    props.isFullscreen,
    canvasZoom,
  ]);

  useEffect(() => {
    if (props.isNewCommentActive) {
      isMouseScrollDisabled.current = true;
    }
  }, [props.isNewCommentActive]);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (
      !isMouseScrollDisabled.current &&
      event.buttons === MouseButtons.left &&
      (event.currentTarget.scrollHeight > event.currentTarget.clientHeight ||
        event.currentTarget.scrollWidth > event.currentTarget.clientWidth)
    ) {
      isDraggingRef.current = true;
      lastXRef.current = (canvasContainer.current?.scrollLeft || 0) + event.clientX;
      lastYRef.current = (canvasContainer.current?.scrollTop || 0) + event.clientY;
      event.preventDefault();
    }
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!isMouseScrollDisabled.current && canvas.current && isDraggingRef.current) {
      canvasContainer.current?.scrollTo(lastXRef.current - event.clientX, lastYRef.current - event.clientY);
    }
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
  };

  useEffect(() => {
    setTimeout(() => {
      if (props.mode === SolutionViewMode.sampleAssessmentGroupAssignment && !props.isCommentOpen) {
        setHiddenContainerHeight(`calc(100% - 10px)`);
      } else if (props.isCommentOpen) {
        const el = document.getElementById('comments-list');
        if (el) {
          const additionalHeightOffset = props.mode === SolutionViewMode.sampleAssessmentGroupAssignment ? 10 : 85;
          setHiddenContainerHeight(`calc(100% - ${el.clientHeight + additionalHeightOffset}px)`);
        }
      } else {
        setHiddenContainerHeight('calc(100% - 85px)');
      }
    }, 200);
  }, [props.isCommentOpen, props.mode, props.activeCriterion, props.activeTask]);

  const rotateAngleIsZero = currentRotateAngle.current === 0;

  const allPageImagesAreLoaded =
    (pageAreAddedToCanvas &&
      pageImages.current.length > 0 &&
      shouldDisplayImageZones &&
      pageImages.current.length === props.imageZones.length) ||
    (!shouldDisplayImageZones && pageImages.current.length >= 1);

  const getCanvasTopPadding = () => {
    return props.comments.length > 0 &&
      props.commentsOnPageVisible &&
      props.comments.some((comment) => comment.y1 && comment.y1 < 300)
      ? 150
      : 0;
  };

  return (
    <>
      <div
        ref={popoversContainer}
        className={
          !props.isFullscreen
            ? `${classes.cardContainer} ${classes.cardContainerNonFullscreen}`
            : `${classes.cardContainer} ${classes.cardContainerFullscreen}`
        }
        onContextMenu={(event) => {
          event.preventDefault();
        }}
        style={{ width: !props.isAnnotationOpenAndDocked ? 'calc(100% - 40px)' : 'calc(100% - 160px)' }}
      >
        <div
          style={{
            overflow: 'hidden',
            height: hiddenContainerHeight,
          }}
          className={
            !props.isFullscreen
              ? `${classes.cardPreview} ${classes.cardPreviewNonFullscreen}`
              : `${classes.cardPreview} ${classes.cardPreviewFullscreen}`
          }
        >
          {canvas.current && canvasZoom && props.examinerFunction !== undefined && allPageImagesAreLoaded && (
            <>
              <CommentsOnPageManager
                canvas={canvas.current}
                canvasZoom={canvasZoom}
                comments={props.comments}
                orgzId={props.orgzId}
                pageImages={pageImages.current}
                commentsOnPageVisible={props.commentsOnPageVisible}
                allCommentsOnPageVisible={props.allCommentsOnPageVisible}
                canvasContainerRef={canvasContainer}
                canEditComment={props.canEditComment}
                rotateAngle={currentRotateAngle.current}
                examinerFunction={props.examinerFunction}
                actionsToTrigger={actionsToTrigger}
                onActionTrigger={handleActionTrigger}
                onCommentChanged={props.onCommentChanged}
                getPopoverCoord={getPopoverCoord}
                getOffsetForPopovers={getOffsetForPopovers}
                getCanvasPoint={getCanvasPoint}
                isNewCommentActive={props.isNewCommentActive}
                setIsNewCommentActive={props.setIsNewCommentActive}
                updateIsMouseScrollDisabled={(value: boolean) => (isMouseScrollDisabled.current = value)}
                previewType={props.previewType}
              />
              <AnnotationsOnPageManagerContextProvider
                canvas={canvas.current}
                pageImages={pageImages.current}
                annotations={props.annotations}
                annotationVersions={props.annotationVersions}
                annotationDefinitions={props.annotationDefinitions}
                annotationsVisible={props.annotationsVisible}
                activeTask={props.activeTask ?? null}
                activeCriterion={props.activeCriterion}
                examinerFunction={props.examinerFunction}
                sampleExamTaskGroupSolutionScoreState={props.sampleExamTaskGroupSolutionScoreState}
                getCanvasPoint={getCanvasPoint}
                canEditRemoveAnnotation={props.canEditRemoveAnnotation && rotateAngleIsZero}
                rotateAngle={currentRotateAngle.current}
                currentCoords={currentCoords.current}
                addingAnnotationEnabled={props.canAddAnnotation}
                canvasElement={props.canvasElementRef}
                activeAnnotationId={props.activeAnnotationId}
                isActiveAnnotationForGrading={props.isActiveAnnotationForGrading}
              >
                <AnnotationsOnPageManager
                  ref={annotationsOnPageManagerRef}
                  canAddAnnotation={props.canAddAnnotation && rotateAngleIsZero}
                  topCanvasMargin={topCanvasMargin}
                  onActionTrigger={handleActionTrigger}
                  onAddAnnotation={props.onAddAnnotation}
                  onBeforeAddAnnotation={props.onBeforeAddAnnotation}
                  onAnnotationChanged={props.onAnnotationChanged}
                  onAnnotationDeleted={props.onAnnotationDeleted}
                  onAnnotationsRefresh={props.onAnnotationsRefresh}
                  onAnnotationDeactivate={props.onAnnotationDeactivate}
                  onCommentChanged={() => props.onCommentChanged(null)}
                  getPopoverCoord={getPopoverCoord}
                  pageAreAddedToCanvas={pageAreAddedToCanvas}
                  examSheetPagesLoaded={props.examSheetPagesLoaded}
                  imagesCount={props.pagesUrls.length}
                  scoreState={props.scoreState}
                  setIsAnnotationSelected={props.setIsAnnotationSelected}
                  selectPreviousActiveAnnotation={props.selectPreviousActiveAnnotation}
                  canvasContainer={canvasContainer}
                  updateIsMouseScrollDisabled={(value: boolean) => (isMouseScrollDisabled.current = value)}
                />
              </AnnotationsOnPageManagerContextProvider>
            </>
          )}
          {props.showErrorPage && props.onRetryImageLoad && (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 10,
              }}
            >
              <ImageLoadErrorPage onRetry={props.onRetryImageLoad} previewCardFullWidth={props.previewCardFullWidth} />
            </div>
          )}
          <div
            style={{
              width: '100%',
              overflow: 'scroll',
              marginTop: '15px',
              paddingTop: getCanvasTopPadding() + 'px',
              display: 'flex',
              justifyContent: 'center',
              visibility: props.showErrorPage ? 'hidden' : 'visible',
            }}
            ref={canvasContainer}
            onScroll={handleScroll}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onDoubleClick={resetCanvasPosition}
          >
            <canvas
              ref={props.canvasElementRef}
              style={{
                marginTop: `${topCanvasMargin}px`,
                marginLeft: `${VIEW_LEFT_MARGIN}px`,
              }}
            />
          </div>
          <TooltipOne
            title={props.isFullscreen ? 'Zamknij pełny ekran' : 'Pełny ekran'}
            placement={props.isFullscreen ? 'left' : 'top'}
          >
            <img
              className={!props.isFullscreen ? classes.fullscreenIcon : classes.fullscreenIconFullscreen}
              style={{
                left: props.previewCardFullWidth
                  ? !props.isFullscreen
                    ? 'calc(100% - 35px)'
                    : 'calc(100% - 35px)'
                  : 'calc(100% - 25px)',
              }}
              onClick={props.handleFullscreen}
              src={!props.isFullscreen ? OpenFullscreen : CloseFullscreen}
              alt="ikona zamknij pełny ekran"
            />
          </TooltipOne>
        </div>
      </div>
    </>
  );
};
